import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import BBALoader from '../components/BBALoader';
import { FaPlus, FaArrowUp, FaArrowDown, FaPencilAlt } from 'react-icons/fa';
import { X } from 'lucide-react';
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc, serverTimestamp, writeBatch } from 'firebase/firestore';
import { db } from '../firebase';
import { useTheme } from '../contexts/ThemeContext';
import BackButton from '../components/BackButton';
import AddButton from '../components/AddButton';
import SaveButton from '../components/SaveButton';
import EditButton from '../components/EditButton';
import SopRichEditor from '../components/sop/SopRichEditor';
import SopRichView from '../components/sop/SopRichView';
import { flattenStepContent } from '../utils/sopContent';
import { migrateJobDescriptionRecord } from '../utils/migrateJobDescriptionRecord';
import { suggestNextVersion } from '../services/hrCollectionsService';
import { fetchGroupEmojis, saveGroupEmoji, defaultGroupEmojis } from '../services/groupEmojiService';
import JdHtmlTemplateEditor from '../components/jobdescription/JdHtmlTemplateEditor';
import PortalBackground from '../components/PortalBackground';

// Same full emoji picker used by the Sales / HR / SOP material templates.
const EmojiPicker = React.lazy(() => import('emoji-picker-react'));

/**
 * CRM Job Description Templates — mirrors the SOP Templates page. Each job
 * description is a single Tiptap document (contentJSON) with emoji, version and
 * a card description. The structured `details` object is left untouched as a
 * permanent fallback (converted on the fly for un-migrated records).
 */
const JobDescriptionTemplates = () => {
  const { darkMode } = useTheme();
  const [activeTab, setActiveTab] = useState('design');
  const [selectedJob, setSelectedJob] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedJob, setEditedJob] = useState(null);
  const [isNewTemplate, setIsNewTemplate] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [emojiPickerMode, setEmojiPickerMode] = useState('card'); // 'card' | 'group'
  const [groupEmojis, setGroupEmojis] = useState(defaultGroupEmojis('jobDescription'));
  const [positions, setPositions] = useState({
    design: [],
    construction: [],
    office: []
  });
  // Footer container node for the editor's formatting toolbar (portaled in).
  const [toolbarSlot, setToolbarSlot] = useState(null);

  useEffect(() => {
    fetchGroupEmojis('jobDescription').then(setGroupEmojis).catch(() => {});
  }, []);

  const tabs = [
    { id: 'design', label: 'Design' },
    { id: 'construction', label: 'Construction' },
    { id: 'office', label: 'Office' },
    // staticEmoji: not a portal group — fixed emoji, no group-emoji pencil.
    { id: 'html-template', label: 'HTML Template', staticEmoji: '🎨' }
  ];

  useEffect(() => {
    fetchJobDescriptions();
  }, []);

  const fetchJobDescriptions = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'jobDescriptions'));
      const jobs = querySnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      const byOrder = (a, b) =>
        (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER);
      setPositions({
        design: jobs.filter((j) => j.department === 'design').sort(byOrder),
        construction: jobs.filter((j) => j.department === 'construction').sort(byOrder),
        office: jobs.filter((j) => j.department === 'office').sort(byOrder)
      });
    } catch (error) {
      console.error('Error fetching job descriptions:', error);
    }
  };

  // Reorder within a department; persist `order` (carries through to the portal).
  const moveJob = async (index, direction) => {
    const list = positions[activeTab] || [];
    const target = index + direction;
    if (target < 0 || target >= list.length) return;

    const next = [...list];
    [next[index], next[target]] = [next[target], next[index]];
    setPositions((prev) => ({ ...prev, [activeTab]: next }));

    try {
      const batch = writeBatch(db);
      next.forEach((job, i) => {
        if (job.id && typeof job.id === 'string') {
          batch.update(doc(db, 'jobDescriptions', job.id), { order: i, updatedAt: serverTimestamp() });
        }
      });
      await batch.commit();
      setPositions((prev) => ({
        ...prev,
        [activeTab]: (prev[activeTab] || []).map((j, i) => ({ ...j, order: i }))
      }));
    } catch (error) {
      console.error('Error reordering job descriptions:', error);
      fetchJobDescriptions();
    }
  };

  const handleJobClick = (job) => {
    const safeJob = {
      id: job.id || null,
      title: job.title || '',
      emoji: job.emoji || '',
      version: job.version || '1.0',
      department: job.department || 'design',
      details: job.details || {},
      // Prefer saved Tiptap JSON; else convert the legacy `details` on the fly.
      contentJSON: flattenStepContent(job.contentJSON || migrateJobDescriptionRecord(job))
    };
    setSelectedJob(safeJob);
    setEditedJob({ ...safeJob });
    setIsNewTemplate(false);
    setIsEditing(false);
    setIsModalOpen(true);
  };

  // CRM sidebar search deep-links land here as ?open=<jobId> — switch to the
  // role's department tab and open its document modal once templates load.
  const [searchParams] = useSearchParams();
  const pendingOpenRef = useRef(searchParams.get('open'));
  useEffect(() => {
    const openId = pendingOpenRef.current;
    if (!openId) return;
    const target = [
      ...(positions.design || []),
      ...(positions.construction || []),
      ...(positions.office || []),
    ].find((j) => j.id === openId);
    if (!target) return;
    pendingOpenRef.current = null;
    setActiveTab(target.department || 'design');
    handleJobClick(target);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positions]);

  const handleEdit = () => setIsEditing(true);

  const bumpVersion = () =>
    setEditedJob((prev) => ({ ...(prev || {}), version: suggestNextVersion(prev?.version || '1.0') }));

  const handleNewTemplate = () => {
    const newTemplate = {
      title: 'New Job Description',
      emoji: '',
      version: '1.0',
      department: activeTab || 'design',
      details: {}
    };
    setSelectedJob(newTemplate);
    setEditedJob(newTemplate);
    setIsNewTemplate(true);
    setIsEditing(true);
    setIsModalOpen(true);
  };

  const handleDeleteJob = async () => {
    if (!window.confirm('Are you sure you want to delete this job description template?')) return;
    try {
      if (selectedJob.id && typeof selectedJob.id === 'string') {
        await deleteDoc(doc(db, 'jobDescriptions', selectedJob.id));
      }
      setPositions((prev) => ({
        ...prev,
        [activeTab]: (prev[activeTab] || []).filter((j) => j.id !== selectedJob.id)
      }));
      setIsModalOpen(false);
      setIsEditing(false);
      setSelectedJob(null);
      setEditedJob(null);
    } catch (error) {
      console.error('Error deleting job description:', error);
    }
  };

  const handleSave = async () => {
    try {
      if (!editedJob || !editedJob.department) return;

      if (isNewTemplate) {
        const jobData = {
          title: editedJob.title || '',
          emoji: editedJob.emoji || '',
          version: editedJob.version || '1.0',
          department: editedJob.department,
          details: editedJob.details || {},
          // Tiptap JSON is the primary content; `details` stays as fallback.
          contentJSON: editedJob.contentJSON || null,
          order: (positions[editedJob.department] || []).length,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        };
        const docRef = await addDoc(collection(db, 'jobDescriptions'), jobData);
        const newJobWithId = { id: docRef.id, ...jobData };
        setPositions((prev) => ({
          ...prev,
          [editedJob.department]: [...(prev[editedJob.department] || []), newJobWithId]
        }));
        setSelectedJob(newJobWithId);
        setEditedJob(newJobWithId);
      } else {
        if (!selectedJob.id || typeof selectedJob.id !== 'string') return;
        const docRef = doc(db, 'jobDescriptions', selectedJob.id);
        // Note: `details` intentionally omitted — leave the legacy fallback intact.
        await updateDoc(docRef, {
          title: editedJob.title || '',
          emoji: editedJob.emoji || '',
          version: editedJob.version || '1.0',
          department: editedJob.department,
          contentJSON: editedJob.contentJSON || null,
          updatedAt: serverTimestamp()
        });

        setPositions((prev) => {
          const nextState = { ...prev };
          if (!nextState[selectedJob.department]) nextState[selectedJob.department] = [];
          if (!nextState[editedJob.department]) nextState[editedJob.department] = [];
          if (selectedJob.department !== editedJob.department) {
            nextState[selectedJob.department] = (prev[selectedJob.department] || []).filter((j) => j.id !== selectedJob.id);
            nextState[editedJob.department] = [...(prev[editedJob.department] || []), { ...editedJob, id: selectedJob.id }];
          } else {
            nextState[editedJob.department] = (prev[editedJob.department] || []).map((j) =>
              j.id === selectedJob.id ? { ...editedJob, id: selectedJob.id } : j
            );
          }
          return nextState;
        });
        setSelectedJob({ ...editedJob, id: selectedJob.id });
      }

      setIsEditing(false);
      setIsNewTemplate(false);
    } catch (error) {
      console.error('Error saving job description:', error);
    }
  };

  const handleCancel = () => {
    if (isNewTemplate) {
      setIsModalOpen(false);
      setIsEditing(false);
      setIsNewTemplate(false);
      setSelectedJob(null);
      setEditedJob(null);
    } else if (selectedJob) {
      setEditedJob({ ...selectedJob });
      setIsEditing(false);
    } else {
      setEditedJob(null);
      setIsEditing(false);
      setIsModalOpen(false);
      setIsNewTemplate(false);
    }
  };

  const handleBack = () => window.history.back();

  return (
    <div className="min-h-screen p-6 relative z-0">
      <PortalBackground />
      <div className="relative max-w-7xl mx-auto">
        <div>
          <div className="flex items-center justify-between mb-8">
            <div className="flex-1 flex items-center">
              <BackButton onClick={handleBack} />
            </div>
            <div className="flex items-center">
              <h1 className={`text-3xl font-bold tracking-tight text-gray-900 dark:text-white`}>Job Description Templates</h1>
            </div>
            <div className="flex-1 flex justify-end">
              {activeTab !== 'html-template' && <AddButton onClick={handleNewTemplate} />}
            </div>
          </div>

          {/* Tabs */}
          <div className={`border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'} mb-8`}>
            <nav className="-mb-px flex justify-center space-x-8" aria-label="Tabs">
              {tabs.map((tab) => {
                const active = activeTab === tab.id;
                return (
                  <div key={tab.id} className={`inline-flex items-center border-b-2 ${active ? 'border-red-500' : 'border-transparent'}`}>
                    <button
                      onClick={() => setActiveTab(tab.id)}
                      className={`whitespace-nowrap py-4 px-1 font-medium text-sm flex items-center ${active ? 'text-red-600' : darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                      <span className="mr-2 text-lg leading-none">{tab.staticEmoji || groupEmojis[tab.id] || '📁'}</span>
                      {tab.label}
                    </button>
                    {active && !tab.staticEmoji && (
                      <button
                        type="button"
                        onClick={() => { setEmojiPickerMode('group'); setShowEmojiPicker(true); }}
                        title="Change the emoji shown on the portal section header for this group"
                        className={`ml-1 p-1 rounded ${darkMode ? 'text-gray-400 hover:text-white hover:bg-gray-700' : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'}`}
                      >
                        <FaPencilAlt className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                );
              })}
            </nav>
          </div>

          {/* Job Description Cards — or the HTML Template editor */}
          {activeTab === 'html-template' ? (
            <JdHtmlTemplateEditor darkMode={darkMode} />
          ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {(positions[activeTab] || []).map((job, index) => (
              <div
                key={job.id || index}
                className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 cursor-pointer relative`}
                onClick={() => handleJobClick(job)}
              >
                <div className="absolute top-2 right-2 flex flex-col gap-0.5">
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); moveJob(index, -1); }}
                    disabled={index === 0}
                    title="Move up"
                    className={`p-1 rounded ${index === 0 ? 'opacity-30 cursor-not-allowed' : darkMode ? 'text-gray-300 hover:bg-gray-500' : 'text-gray-500 hover:bg-gray-200'}`}
                  >
                    <FaArrowUp className="w-3 h-3" />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); moveJob(index, 1); }}
                    disabled={index === (positions[activeTab] || []).length - 1}
                    title="Move down"
                    className={`p-1 rounded ${index === (positions[activeTab] || []).length - 1 ? 'opacity-30 cursor-not-allowed' : darkMode ? 'text-gray-300 hover:bg-gray-500' : 'text-gray-500 hover:bg-gray-200'}`}
                  >
                    <FaArrowDown className="w-3 h-3" />
                  </button>
                </div>
                <div className="flex flex-col items-center mb-4">
                  {job.emoji && <span className="text-3xl leading-none mb-2">{job.emoji}</span>}
                  <h3 className={`text-lg font-semibold text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>{job.title || 'Untitled'}</h3>
                  <span className={`mt-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold leading-none ${darkMode ? 'bg-gray-800 text-gray-400 border border-gray-600' : 'bg-gray-100 text-gray-500 border border-gray-200'}`}>
                    v{job.version || '1.0'}
                  </span>
                </div>
                <button className="w-full bg-red-600 text-white py-1.5 px-3 rounded-md hover:bg-red-700 transition-colors duration-200 text-sm">
                  View Details
                </button>
              </div>
            ))}
          </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && selectedJob && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[1001]">
          <div className={`bg-white dark:bg-gray-900 rounded-2xl max-w-4xl w-full max-h-[90vh] flex flex-col relative`}>
            <button
              type="button"
              onClick={() => setIsModalOpen(false)}
              title="Close"
              aria-label="Close"
              className={`absolute top-3 left-3 z-10 p-1.5 rounded-full transition ${darkMode ? 'text-gray-400 hover:text-white hover:bg-gray-700' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'}`}
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex-1 overflow-y-auto thin-scrollbar px-8 pt-12 pb-4">
              <div className="flex flex-col items-center mb-8">
                {isEditing ? (
                  <input
                    type="text"
                    value={editedJob?.title || ''}
                    onChange={(e) => setEditedJob({ ...(editedJob || {}), title: e.target.value })}
                    className={`text-3xl font-bold mb-2 text-center border rounded px-2 py-1 ${darkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'}`}
                  />
                ) : (
                  <h2 className={`text-3xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{selectedJob.title}</h2>
                )}
              </div>

              <div className="space-y-6">
                {(isNewTemplate || isEditing) && (
                  <div className={`bg-gray-50 dark:bg-gray-800/60 rounded-xl p-6 border border-gray-200 dark:border-gray-800`}>
                    <div className="grid grid-cols-3 gap-4">
                      {/* Department */}
                      <div className="flex flex-col items-center text-center">
                        <h3 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Department</h3>
                        <select
                          value={editedJob?.department || 'design'}
                          onChange={(e) => setEditedJob({ ...(editedJob || {}), department: e.target.value })}
                          className={`w-full border rounded px-2 py-2 text-sm text-center ${darkMode ? 'bg-gray-600 text-white border-gray-500' : 'bg-white border-gray-300'}`}
                        >
                          <option value="design">Design</option>
                          <option value="construction">Construction</option>
                          <option value="office">Office</option>
                        </select>
                      </div>
                      {/* Card Emoji */}
                      <div className="flex flex-col items-center text-center">
                        <h3 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Card Emoji</h3>
                        <button
                          type="button"
                          onClick={() => { setEmojiPickerMode('card'); setShowEmojiPicker(true); }}
                          className={`w-16 h-16 flex items-center justify-center rounded-xl border text-3xl transition-colors bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700`}
                          title="Choose an emoji"
                        >
                          {editedJob?.emoji ? (
                            <span className="leading-none">{editedJob.emoji}</span>
                          ) : (
                            <FaPlus className={`text-base ${darkMode ? 'text-gray-300' : 'text-gray-400'}`} />
                          )}
                        </button>
                      </div>
                      {/* Version — click to bump */}
                      <div className="flex flex-col items-center text-center">
                        <h3 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Version</h3>
                        <button
                          type="button"
                          onClick={bumpVersion}
                          title="Click to update to the next version"
                          className={`inline-flex items-center gap-1.5 px-4 h-16 rounded-lg border text-lg font-semibold transition-colors ${darkMode ? 'bg-gray-600 border-gray-500 text-gray-100 hover:bg-gray-500' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-100'}`}
                        >
                          v{editedJob?.version || '1.0'}
                          <FaArrowUp className="text-xs text-gray-400" />
                        </button>
                        <p className={`mt-1 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          Click to update.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {isEditing ? (
                  <SopRichEditor
                    mode="full"
                    value={editedJob?.contentJSON}
                    onChange={(json) => setEditedJob((prev) => ({ ...(prev || {}), contentJSON: json }))}
                    darkMode={darkMode}
                    toolbarSlot={toolbarSlot}
                  />
                ) : (
                  <SopRichView
                    value={selectedJob.contentJSON || migrateJobDescriptionRecord(selectedJob)}
                    darkMode={darkMode}
                  />
                )}
              </div>
            </div>

              {/* Action Buttons — always visible */}
              <div className={`shrink-0 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'} px-8 py-4 flex justify-between items-center`}>
                <div className="flex items-center gap-4">
                  {isEditing && <div ref={setToolbarSlot} />}
                  {isEditing && !isNewTemplate && (
                    <button
                      onClick={handleDeleteJob}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      Delete
                    </button>
                  )}
                </div>
                <div className="flex space-x-4">
                  {isEditing ? (
                    <>
                      <button
                        onClick={handleCancel}
                        className={`px-4 py-2 text-sm font-medium border rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 ${darkMode ? 'text-gray-300 bg-gray-700 border-gray-600 hover:bg-gray-600' : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'}`}
                      >
                        Cancel
                      </button>
                      <SaveButton onClick={handleSave} variant="green" />
                    </>
                  ) : (
                    <EditButton label="Edit" onClick={handleEdit} />
                  )}
                </div>
              </div>
          </div>
        </div>
      )}

      {/* Full emoji picker */}
      {showEmojiPicker && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[1100]"
          onClick={() => setShowEmojiPicker(false)}
        >
          <div
            className={`bg-white dark:bg-gray-900 rounded-2xl p-4 w-full max-w-sm`}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className={`text-lg font-semibold mb-3 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>Choose an icon</h3>
            <div className="flex justify-center">
              <React.Suspense
                fallback={
                  <div className="flex items-center justify-center h-72 w-full">
                    <BBALoader size={32} />
                  </div>
                }
              >
                <EmojiPicker
                  onEmojiClick={async (emojiData) => {
                    if (emojiPickerMode === 'group') {
                      const updated = await saveGroupEmoji('jobDescription', activeTab, emojiData.emoji, groupEmojis);
                      setGroupEmojis(updated);
                    } else {
                      setEditedJob((prev) => ({ ...(prev || {}), emoji: emojiData.emoji }));
                    }
                    setShowEmojiPicker(false);
                  }}
                  theme={darkMode ? 'dark' : 'light'}
                  width="100%"
                  height={380}
                  lazyLoadEmojis
                  previewConfig={{ showPreview: false }}
                />
              </React.Suspense>
            </div>
            <div className="flex items-center justify-between mt-3">
              <button
                type="button"
                onClick={async () => {
                  if (emojiPickerMode === 'group') {
                    const updated = await saveGroupEmoji('jobDescription', activeTab, '', groupEmojis);
                    setGroupEmojis(updated);
                  } else {
                    setEditedJob((prev) => ({ ...(prev || {}), emoji: '' }));
                  }
                  setShowEmojiPicker(false);
                }}
                className="px-3 py-2 rounded-md text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30"
              >
                {emojiPickerMode === 'group' ? 'Use default' : 'Clear emoji'}
              </button>
              <button
                type="button"
                onClick={() => setShowEmojiPicker(false)}
                className={`px-3 py-2 text-sm font-medium border rounded-md ${darkMode ? 'text-gray-300 bg-gray-700 border-gray-600 hover:bg-gray-600' : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'}`}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobDescriptionTemplates;
